import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsRun as RunIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { DailyActivity, User } from '../../types';
import { activityService } from '../../services/activityService';
import { getActivityMiles, isWorkoutSplitMilesActivity } from '../../utils/activityUtils';
import RunningActivityModal from '../activities/RunningActivityModal';

interface AthleteActivityEditorProps {
  open: boolean;
  onClose: () => void;
  athlete: User;
  teamId: string;
}

const AthleteActivityEditor: React.FC<AthleteActivityEditorProps> = ({
  open,
  onClose,
  athlete,
  teamId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activities, setActivities] = useState<DailyActivity[]>([]);
  const [editingActivity, setEditingActivity] = useState<DailyActivity | null>(null);
  const [deletingActivity, setDeletingActivity] = useState<DailyActivity | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadActivities();
      setError('');
    }
  }, [open, athlete.id, teamId]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const history = await activityService.getAthleteHealthMetricsHistory(athlete.id, 60, teamId);
      setActivities(history);
    } catch (err: any) {
      setError('Failed to load activities');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (activity: DailyActivity) => {
    setEditingActivity(activity);
  };

  const handleCloseEditModal = () => {
    setEditingActivity(null);
    loadActivities();
  };

  const handleDeleteClick = (activity: DailyActivity) => {
    setDeletingActivity(activity);
  };

  const handleConfirmDelete = async () => {
    if (!deletingActivity) return;
    setDeleteLoading(true);
    setError('');
    try {
      const success = await activityService.deleteActivity(deletingActivity.id);
      if (success) {
        setDeletingActivity(null);
        loadActivities();
      } else {
        setError('Failed to delete activity');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete activity');
      console.error(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeletingActivity(null);
  };

  const formatRunDisplay = (activity: DailyActivity): string => {
    if (activity.runType === 'Strength' && (activity.strengthMinutes || 0) > 0) {
      return `${activity.strengthMinutes} min strength`;
    }
    if (activity.runType === 'Cross Training' && (activity.crossTrainingMinutes || 0) > 0) {
      return `${activity.crossTrainingMinutes} min cross training`;
    }
    if (isWorkoutSplitMilesActivity(activity)) {
      const total = getActivityMiles(activity);
      return total > 0 ? `${total} mi` : '-';
    }
    if ((activity.milesRun || 0) > 0) {
      return `${activity.milesRun} mi`;
    }
    if (activity.runType === 'Rest Day' || activity.runType === 'Sick' || activity.runType === 'Injured') {
      return activity.runType;
    }
    return activity.runType || '-';
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RunIcon />
            Edit Activities - {athlete.displayName}
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select an activity to edit or delete. Editing allows you to change run type, miles, cross training, and health metrics.
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Run Type</TableCell>
                    <TableCell align="right">Miles / CT</TableCell>
                    <TableCell align="center">Health (S/H/N)</TableCell>
                    <TableCell align="center">Points</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activities.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No activities found for this athlete
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    activities.map((activity) => {
                      const healthAvg = (
                        (activity.sleepScore + activity.hydrationLevel + activity.nutritionLevel) /
                        3
                      ).toFixed(1);
                      return (
                        <TableRow key={activity.id} hover>
                          <TableCell>
                            {format(parseISO(activity.date), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>{activity.runType || '-'}</TableCell>
                          <TableCell align="right">
                            {formatRunDisplay(activity)}
                          </TableCell>
                          <TableCell align="center">
                            {activity.sleepScore}/{activity.hydrationLevel}/{activity.nutritionLevel} (avg {healthAvg})
                          </TableCell>
                          <TableCell align="center">
                            {activity.pointsEarned ?? '-'}
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title="Edit activity">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(activity)}
                                aria-label="Edit activity"
                                sx={{ color: 'primary.main' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete activity">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(activity)}
                                aria-label="Delete activity"
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deletingActivity}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete activity?</DialogTitle>
        <DialogContent>
          {deletingActivity && (
            <Typography>
              Are you sure you want to delete the activity for{' '}
              <strong>{format(parseISO(deletingActivity.date), 'MMM d, yyyy')}</strong> (
              {deletingActivity.runType || 'None'})? This cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Running Activity Modal for editing - opens on top when Edit is clicked */}
      <RunningActivityModal
        open={!!editingActivity}
        onClose={handleCloseEditModal}
        onActivitySubmitted={() => handleCloseEditModal()}
        teamId={teamId}
        editingActivity={editingActivity || undefined}
      />
    </>
  );
};

export default AthleteActivityEditor;
